import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, MapPin, Clock, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Prenotazione() {
  const [sedi, setSedi] = useState([]);
  const [sede, setSede] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    preferenze: "",
    motivo: "",
    privacy: false,
  });

  useEffect(() => {
    axios
      .get(`${API}/sedi`)
      .then((r) => setSedi(r.data))
      .catch(() => toast.error("Impossibile caricare le sedi"));
  }, []);

  const canSubmit =
    sede &&
    form.nome &&
    form.cognome &&
    form.email &&
    form.telefono &&
    form.motivo &&
    form.privacy &&
    !submitting;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Completa tutti i campi richiesti");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/prenotazioni`, { ...form, sede });
      setSuccess(res.data);
      toast.success("Richiesta di prenotazione inviata");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore invio richiesta";
      toast.error(typeof msg === "string" ? msg : "Errore invio richiesta");
    } finally {
      setSubmitting(false);
    }
  }

  const sedeSelezionata = sedi.find((s) => s.id === sede);

  if (success) {
    return (
      <section id="prenota" data-testid="prenotazione-section" className="py-24 md:py-32 bg-[#EFECE7]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-[#E5E1D8] rounded-2xl p-12"
            data-testid="prenotazione-success"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-[#8A6D56] grid place-items-center">
              <Check className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-8 text-[#1A1D1A]">
              Richiesta ricevuta
            </h2>
            <p className="text-[#5C635E] mt-4 max-w-xl mx-auto">
              Grazie {success.nome}. Ho ricevuto la tua richiesta per la sede di{" "}
              <strong className="text-[#1A1D1A]">
                {success.sede === "san-matteo" ? "Pavia" : "Voghera"}
              </strong>
              . La segreteria ti contatterà per confermare data e orario.
            </p>
            <button
              onClick={() => {
                setSuccess(null);
                setSede("");
                setForm({ nome: "", cognome: "", email: "", telefono: "", preferenze: "", motivo: "", privacy: false });
              }}
              data-testid="nuova-prenotazione-btn"
              className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full border border-[#8A6D56] text-[#8A6D56] text-sm font-semibold hover:bg-[#8A6D56] hover:text-white transition-colors"
            >
              Nuova richiesta
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="prenota"
      data-testid="prenotazione-section"
      className="py-24 md:py-32 bg-[#EFECE7]"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left context — disponibilità sedi */}
        <div className="lg:col-span-4">
          <p className="eyebrow">Prenotazione</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 text-[#1A1D1A]">
            Richiedi una <span className="italic text-[#8A6D56]">visita</span>.
          </h2>
          <p className="text-[#5C635E] leading-relaxed mt-6">
            Compila il modulo indicando la sede preferita: la segreteria ti
            ricontatterà per concordare data e orario.
          </p>

          <div className="mt-10 space-y-6">
            <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6">
              <p className="eyebrow text-[#8A6D56]">Pavia</p>
              <p className="font-serif-display text-xl leading-tight mt-3 text-[#1A1D1A]">
                Policlinico San Matteo
              </p>
              <div className="mt-4 space-y-2 text-sm text-[#1A1D1A]/85">
                <div className="flex items-start gap-2">
                  <CalendarDays className="w-4 h-4 mt-0.5 text-[#8A6D56] shrink-0" strokeWidth={1.5} />
                  <span>Lun · Mar · Mer · Gio</span>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 text-[#8A6D56] shrink-0" strokeWidth={1.5} />
                  <div className="flex flex-col">
                    <span>Visita 16:00 — 18:00</span>
                    <span className="text-xs text-[#5C635E]">Prenotazioni telefoniche 09:00 — 15:00</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6">
              <p className="eyebrow text-[#8A6D56]">Voghera</p>
              <p className="font-serif-display text-xl leading-tight mt-3 text-[#1A1D1A]">
                Studio Viqueria
              </p>
              <div className="mt-4 space-y-2 text-sm text-[#1A1D1A]/85">
                <div className="flex items-start gap-2">
                  <CalendarDays className="w-4 h-4 mt-0.5 text-[#8A6D56] shrink-0" strokeWidth={1.5} />
                  <span>Su appuntamento — data da concordare</span>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 text-[#8A6D56] shrink-0" strokeWidth={1.5} />
                  <div className="flex flex-col">
                    <span>Visita 09:30 — 13:00</span>
                    <span className="text-xs text-[#5C635E]">Prenotazioni telefoniche 09:00 — 15:00</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-[#5C635E] leading-relaxed">
              La disponibilità effettiva verrà confermata dalla segreteria in fase di
              conferma dell&apos;appuntamento.
            </p>
          </div>
        </div>

        {/* Right form */}
        <form onSubmit={submit} className="lg:col-span-8 bg-white border border-[#E5E1D8] rounded-2xl p-6 md:p-10 space-y-8" data-testid="booking-form">
          {/* Sede */}
          <div>
            <Label htmlFor="sede" className="eyebrow">Sede preferita</Label>
            <Select value={sede} onValueChange={setSede}>
              <SelectTrigger
                id="sede"
                data-testid="select-sede"
                className="mt-3 h-12 rounded-xl border-[#E5E1D8] bg-[#F9F8F6] text-[#1A1D1A]"
              >
                <SelectValue placeholder="Seleziona una sede" />
              </SelectTrigger>
              <SelectContent>
                {sedi.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`select-sede-option-${s.id}`}>
                    {s.nome} — {s.citta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sedeSelezionata && (
              <p className="text-xs text-[#5C635E] mt-3 flex items-center gap-2">
                <MapPin className="w-3 h-3 text-[#8A6D56]" strokeWidth={1.8} />
                Giorni di ricevimento: {sedeSelezionata.giorni.join(", ")} — {sedeSelezionata.orario}
              </p>
            )}
          </div>

          <div className="divider-rule" />

          {/* Dati paziente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="nome" className="eyebrow">Nome</Label>
              <Input
                id="nome"
                data-testid="input-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="mt-3 h-12 rounded-xl border-[#E5E1D8] bg-[#F9F8F6]"
                required
              />
            </div>
            <div>
              <Label htmlFor="cognome" className="eyebrow">Cognome</Label>
              <Input
                id="cognome"
                data-testid="input-cognome"
                value={form.cognome}
                onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                className="mt-3 h-12 rounded-xl border-[#E5E1D8] bg-[#F9F8F6]"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="input-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-3 h-12 rounded-xl border-[#E5E1D8] bg-[#F9F8F6]"
                required
              />
            </div>
            <div>
              <Label htmlFor="telefono" className="eyebrow">Telefono</Label>
              <Input
                id="telefono"
                data-testid="input-telefono"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="mt-3 h-12 rounded-xl border-[#E5E1D8] bg-[#F9F8F6]"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="preferenze" className="eyebrow">Preferenze di giorno / orario (facoltativo)</Label>
            <Input
              id="preferenze"
              data-testid="input-preferenze"
              value={form.preferenze}
              onChange={(e) => setForm({ ...form, preferenze: e.target.value })}
              placeholder="Es. preferibilmente giovedì mattina"
              className="mt-3 h-12 rounded-xl border-[#E5E1D8] bg-[#F9F8F6]"
            />
          </div>

          <div>
            <Label htmlFor="motivo" className="eyebrow">Motivo della visita</Label>
            <Textarea
              id="motivo"
              data-testid="input-motivo"
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              rows={4}
              placeholder="Descrivi brevemente il motivo della visita (es. valori alterati, follow-up, second opinion…)"
              className="mt-3 rounded-xl border-[#E5E1D8] bg-[#F9F8F6]"
              required
            />
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="privacy"
              data-testid="checkbox-privacy"
              checked={form.privacy}
              onCheckedChange={(v) => setForm({ ...form, privacy: v === true })}
              className="mt-1 border-[#8A6D56] data-[state=checked]:bg-[#8A6D56] data-[state=checked]:border-[#8A6D56]"
            />
            <Label htmlFor="privacy" className="text-xs text-[#5C635E] leading-relaxed cursor-pointer">
              Ho letto l&apos;informativa privacy (Reg. UE 2016/679) e acconsento al
              trattamento dei dati personali ai fini della gestione della richiesta.
            </Label>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="submit-prenotazione"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#8A6D56] text-white text-sm font-semibold hover:bg-[#735A47] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {submitting ? "Invio in corso…" : "Invia richiesta"}
              <span aria-hidden>→</span>
            </button>
            <p className="text-xs text-[#5C635E]">
              La segreteria ti ricontatterà per concordare data e orario.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
